/** @file View camera: { x, y, zoom } <-> geometry Camera, screen <-> world, zoom to cursor, pan, fit. */
import { affine } from '@pose2d/math/affine';
import { clamp } from '@pose2d/math/clamp';
import type { Vec2 } from '@pose2d/math/types';
import { fitCamera } from '@pose2d/geometry/camera';
import type { Camera } from '@pose2d/geometry/types';
import { readNum } from './_internal/readNum';

/** Pan/zoom state (the editor CameraSlice shape): world point at the viewport centre and pixels per world unit. */
export interface ViewCamera {
  x: number;
  y: number;
  zoom: number;
}

const MIN_ZOOM = 1 / 64;
const MAX_ZOOM = 256;

/** View-camera math; world is y-up, screen pixels are y-down with the origin at the top-left. */
export const viewCamera = {
  /** Smallest zoom (pixels per world unit) any operation produces. */
  MIN_ZOOM,
  /** Largest zoom (pixels per world unit) any operation produces. */
  MAX_ZOOM,
  /** Builds the geometry Camera of a width x height viewport showing the view. */
  toCamera(view: ViewCamera, width: number, height: number): Camera {
    const z = view.zoom;
    return { worldToPixel: [z, 0, 0, -z, width / 2 - view.x * z, height / 2 + view.y * z], width, height };
  },
  /** World point under screen pixel (sx, sy); writes into out and returns it. */
  screenToWorld(camera: Camera, sx: number, sy: number, out: Vec2): Vec2 {
    return affine.apply(affine.invert(new Float64Array(6), camera.worldToPixel), sx, sy, out);
  },
  /** Screen pixel of world point (wx, wy); writes into out and returns it. */
  worldToScreen(camera: Camera, wx: number, wy: number, out: Vec2): Vec2 {
    return affine.apply(camera.worldToPixel, wx, wy, out);
  },
  /** World units covered by one screen pixel (1 / zoom for a view camera). */
  worldPerPixel(camera: Camera): number {
    const m = camera.worldToPixel;
    return 1 / Math.sqrt(Math.abs(readNum(m, 0) * readNum(m, 3) - readNum(m, 1) * readNum(m, 2)));
  },
  /** Multiplies the zoom by factor (clamped) while the world point under (sx, sy) stays put. */
  zoomAt(view: ViewCamera, width: number, height: number, sx: number, sy: number, factor: number): ViewCamera {
    const zoom = clamp(view.zoom * factor, MIN_ZOOM, MAX_ZOOM);
    const dx = sx - width / 2;
    const dy = sy - height / 2;
    return { x: view.x + dx / view.zoom - dx / zoom, y: view.y - dy / view.zoom + dy / zoom, zoom };
  },
  /** Moves the content by (dx, dy) screen pixels, as when the canvas is dragged. */
  pan(view: ViewCamera, dx: number, dy: number): ViewCamera {
    return { x: view.x - dx / view.zoom, y: view.y + dy / view.zoom, zoom: view.zoom };
  },
  /** Centres world bounds [minX, minY, maxX, maxY] with pad pixels of margin (fitCamera zoom, clamped). */
  fit(bounds: ArrayLike<number>, width: number, height: number, pad: number): ViewCamera {
    const zoom = clamp(readNum(fitCamera(bounds, width, height, pad).worldToPixel, 0), MIN_ZOOM, MAX_ZOOM);
    return { x: (readNum(bounds, 0) + readNum(bounds, 2)) / 2, y: (readNum(bounds, 1) + readNum(bounds, 3)) / 2, zoom };
  },
};
